import React, { useState, useEffect } from 'react';
import {
  Calculator, Download, Calendar, Plus, Trash2, Eye, X,
  AlertCircle, Check, Search, Filter, Wallet, TrendingUp,
  Users, FileText, ChevronRight, Clock, Loader2, RefreshCw,
  FileSpreadsheet, Settings, Table
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { payrollService, Payroll as PayrollType, PayrollDetail, PayrollCalculation, PayrollStats, PayrollReport, PayrollFilter } from '../services';
import { departmentService, Department } from '../services';
import { employeeService } from '../services';
// 2026-08-15 (kullanici talebi): Bordroda da puantajdaki gibi santiye/lokasyon filtresi
import SiteLocationFilter from '../components/SiteLocationFilter';
import { uyar, hata, onayla } from '../components/GlobalModal';

const Payroll = () => {
  const { t } = useTranslation();

  // Tab state
  const [activeTab, setActiveTab] = useState<'payrolls' | 'detailed'>('payrolls');

  // State
  const [payrolls, setPayrolls] = useState<PayrollType[]>([]);
  const [stats, setStats] = useState<PayrollStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  // Calculation state
  const [calculation, setCalculation] = useState<PayrollCalculation | null>(null);
  const [calcYear, setCalcYear] = useState(new Date().getFullYear());
  const [calcMonth, setCalcMonth] = useState(new Date().getMonth() + 1);
  // 2026-08-15 (kullanici talebi): tek personel icin bordro. 0 = tum personel.
  const [calcEmployeeId, setCalcEmployeeId] = useState<number>(0);
  const [calcEmployees, setCalcEmployees] = useState<{ id: number; ad: string }[]>([]);
  const [showCalculator, setShowCalculator] = useState(false);

  // Detail modal
  const [selectedPayroll, setSelectedPayroll] = useState<PayrollDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Detailed report state
  const [detailedReport, setDetailedReport] = useState<PayrollReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [reportDepartmentId, setReportDepartmentId] = useState<number | null>(null);
  // 2026-08-15 (kullanici talebi): Detayli raporda tek personel secimi.
  // Secilince rapor, Excel ve PDF yalnizca o personeli kapsar.
  const [reportEmployeeId, setReportEmployeeId] = useState<number | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Santiye / lokasyon filtresi — 'all' = tumu
  const [reportSiteId, setReportSiteId] = useState<string>('all');
  const [reportLocationId, setReportLocationId] = useState<string>('all');

  // Months
  const monthKeys = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];
  const months = monthKeys.map(key => t(`months.${key}`));

  // Format currency as TL
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Load data
  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [payrollsRes, statsRes, deptsRes] = await Promise.all([
        payrollService.getAll(selectedYear, selectedMonth || undefined),
        payrollService.getStats(selectedYear),
        departmentService.getAll()
      ]);

      if (payrollsRes.success && payrollsRes.data) {
        setPayrolls(payrollsRes.data);
      }
      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
      if (deptsRes.success && deptsRes.data) {
        setDepartments(deptsRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load detailed report
  const loadDetailedReport = async () => {
    setLoadingReport(true);
    try {
      const filter: PayrollFilter = {
        yil: selectedYear,
        ay: selectedMonth || new Date().getMonth() + 1,
        departmentId: reportDepartmentId || undefined,
        employeeId: reportEmployeeId || undefined,
        siteId: reportSiteId !== 'all' ? Number(reportSiteId) : undefined,
        locationId: reportLocationId !== 'all' ? Number(reportLocationId) : undefined,
        sadeceCalisanlar: true
      };
      const response = await payrollService.getReport(filter);
      if (response.success && response.data) {
        setDetailedReport(response.data);
      }
    } catch (error) {
      console.error('Error loading detailed report:', error);
    } finally {
      setLoadingReport(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const filter: PayrollFilter = {
        yil: selectedYear,
        ay: selectedMonth || new Date().getMonth() + 1,
        departmentId: reportDepartmentId || undefined,
        employeeId: reportEmployeeId || undefined,
        siteId: reportSiteId !== 'all' ? Number(reportSiteId) : undefined,
        locationId: reportLocationId !== 'all' ? Number(reportLocationId) : undefined,
        sadeceCalisanlar: true
      };
      await payrollService.exportToExcel(filter);
    } catch (error) {
      console.error('Excel export error:', error);
      uyar(t('payroll.excelExportError'));
    } finally {
      setExportingExcel(false);
    }
  };

  // 2026-08-15 (kullanici talebi): Secili personelin bordrosunu PDF indir.
  // Backend ucu: GET /payroll/report/employee/{id}/pdf
  const handleExportPdf = async () => {
    if (!reportEmployeeId) {
      uyar('PDF bordro icin once bir personel secin.');
      return;
    }
    setExportingPdf(true);
    try {
      const ay = selectedMonth || new Date().getMonth() + 1;
      const res = await fetch(
        `/api/payroll/report/employee/${reportEmployeeId}/pdf?year=${selectedYear}&month=${ay}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        let mesaj = `Sunucu hatasi (${res.status})`;
        try { mesaj = (await res.json())?.message || mesaj; } catch { /* pdf degil */ }
        uyar('PDF olusturulamadi: ' + mesaj);
        return;
      }
      const blob = await res.blob();
      const ad = calcEmployees.find(x => x.id === reportEmployeeId)?.ad ?? 'personel';
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bordro_${ad.replace(/\s+/g, '_')}_${selectedYear}_${String(ay).padStart(2, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e: any) {
      uyar('PDF indirilemedi: ' + (e?.message || 'bilinmeyen hata'));
    } finally {
      setExportingPdf(false);
    }
  };

  // Load detailed report when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'detailed') {
      loadDetailedReport();
    }
  }, [activeTab, selectedYear, selectedMonth, reportDepartmentId, reportEmployeeId, reportSiteId, reportLocationId]);

  // Bordro modali icin personel listesi (tek personel secimi)
  useEffect(() => {
    (async () => {
      try {
        const res = await employeeService.getAll(true);
        if (res.success && res.data) {
          setCalcEmployees(
            res.data
              .map((e: any) => ({
                id: e.employeeId || e.id,
                ad: `${e.firstName || ''} ${e.lastName || ''}`.trim() || (e.employeeCode ?? '-'),
              }))
              .sort((a: any, b: any) => a.ad.localeCompare(b.ad, 'tr'))
          );
        }
      } catch {
        setCalcEmployees([]);   // liste gelmezse "Tum personel" ile devam edilir
      }
    })();
  }, []);

  // Calculate payroll
  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const response = await payrollService.calculate(calcYear, calcMonth, calcEmployeeId);
      if (response.success && response.data) {
        setCalculation(response.data);
      } else {
        uyar(response.message || t('payroll.calculationError'));
      }
    } catch (error) {
      console.error('Calculation error:', error);
      uyar(t('payroll.calculationError'));
    } finally {
      setCalculating(false);
    }
  };

  // Save payroll
  const handleSavePayroll = async () => {
    if (!calculation) return;

    setSaving(true);
    try {
      const salaries = calculation.employees.map(emp => ({
        employeeId: emp.employeeId,
        baseSalary: emp.baseSalary,
        allowances: emp.totalAllowances,
        deductions: emp.totalDeductions,
        netSalary: emp.netSalary,
        // 2026-08-15 KRITIK: incomeTaxBase kaydedilmezse kumulatif gelir vergisi
        // matrahi her ay 0 okunur ve personel yil boyu %15 diliminde kalir —
        // asgari ucret ustu alan herkeste EKSIK vergi kesilir.
        grossSalary: (emp as any).baseSalary,
        incomeTaxBase: (emp as any).incomeTaxBase,
        incomeTax: (emp as any).incomeTax,
        stampTax: (emp as any).stampTax,
        sgkEmployee: (emp as any).sgkEmployee,
        unemploymentEmployee: (emp as any).unemploymentEmployee,
        overtimeHours: (emp as any).overtimeHours,
        workDays: (emp as any).workDays
      }));

      const response = await payrollService.create({
        year: calculation.year,
        month: calculation.month,
        salaries
      });

      if (response.success) {
        uyar(t('payroll.saveSuccess'));
        setCalculation(null);
        setShowCalculator(false);
        loadData();
      } else {
        uyar(response.message || t('payroll.saveError'));
      }
    } catch (error) {
      console.error('Save error:', error);
      uyar(t('payroll.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // View payroll detail
  const handleViewDetail = async (id: number) => {
    setLoadingDetail(true);
    try {
      const response = await payrollService.getById(id);
      if (response.success && response.data) {
        setSelectedPayroll(response.data);
      }
    } catch (error) {
      console.error('Error loading detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Delete payroll
  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleting(true);
    try {
      const response = await payrollService.delete(deleteId);
      if (response.success) {
        setDeleteId(null);
        loadData();
      } else {
        uyar(response.message || t('payroll.deleteError'));
      }
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Years for filter
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('payroll.title')}</h1>
          <p className="text-slate-500 text-sm font-medium">{t('payroll.subtitle')}</p>
        </div>
        <div className="flex gap-3 w-full lg:w-auto">
          <button
            onClick={() => loadData()}
            className="flex-1 lg:flex-none bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition-all shadow-sm"
          >
            <RefreshCw size={18} className="text-slate-500" /> {t('payroll.refresh')}
          </button>
          {activeTab === 'detailed' && (
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel || !detailedReport}
              className="flex-1 lg:flex-none bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-green-700 shadow-xl shadow-green-100 transition-all active:scale-95 disabled:opacity-50"
            >
              {exportingExcel ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
              {t('payroll.downloadExcel')}
            </button>
          )}
          <button
            onClick={() => setShowCalculator(true)}
            className="flex-1 lg:flex-none bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all active:scale-95"
          >
            <Calculator size={18} /> {t('payroll.newCalculation')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-slate-100 p-1.5 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('payrolls')}
          className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'payrolls'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <FileText size={16} />
            {t('payroll.savedPayrolls')}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'detailed'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Table size={16} />
            {t('payroll.detailedReport')}
          </div>
        </button>
      </div>

      {/* PAYROLLS TAB CONTENT */}
      {activeTab === 'payrolls' && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-5">
            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner">
              <Wallet size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('payroll.totalNet')}</p>
              <h3 className="text-xl font-black text-slate-800">{formatCurrency(stats.totalNetSalary)}</h3>
            </div>
          </div>
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-5">
            <div className="w-14 h-14 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center shadow-inner">
              <TrendingUp size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('payroll.totalGross')}</p>
              <h3 className="text-xl font-black text-slate-800">{formatCurrency(stats.totalBaseSalary)}</h3>
            </div>
          </div>
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-5">
            <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center shadow-inner">
              <FileText size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('payroll.payrollCount')}</p>
              <h3 className="text-xl font-black text-slate-800">{stats.payrollCount}</h3>
            </div>
          </div>
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-5">
            <div className="w-14 h-14 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center shadow-inner">
              <Users size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('payroll.totalDeductions')}</p>
              <h3 className="text-xl font-black text-slate-800">{formatCurrency(stats.totalDeductions)}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex gap-3 items-center">
          <Filter size={18} className="text-slate-400" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            value={selectedMonth || ''}
            onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : null)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('payroll.allMonths')}</option>
            {months.map((month, idx) => (
              <option key={idx} value={idx + 1}>{month}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Payroll List */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <Loader2 size={48} className="mx-auto text-blue-500 animate-spin mb-4" />
            <p className="text-slate-400 font-bold">{t('payroll.loading')}</p>
          </div>
        ) : payrolls.length === 0 ? (
          <div className="p-20 text-center">
            <FileText size={48} className="mx-auto text-slate-200 mb-4" />
            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">{t('payroll.noRecords')}</p>
            <button
              onClick={() => setShowCalculator(true)}
              className="mt-4 text-blue-600 font-bold text-sm hover:underline"
            >
              {t('payroll.createFirst')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('payroll.period')}</th>
                  <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('payroll.dateRange')}</th>
                  <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('payroll.createdDate')}</th>
                  <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">{t('payroll.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payrolls.map(payroll => (
                  <tr key={payroll.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-black text-sm">
                          {payroll.month}
                        </div>
                        <div>
                          <div className="font-bold text-slate-800">{payroll.monthName} {payroll.year}</div>
                          <div className="text-xs text-slate-400">{t('payroll.payrollNumber', { id: payroll.id })}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-5">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar size={16} className="text-slate-400" />
                        {payroll.startDate && payroll.endDate && (
                          <>
                            {new Date(payroll.startDate).toLocaleDateString('tr-TR')} - {new Date(payroll.endDate).toLocaleDateString('tr-TR')}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="p-5">
                      <div className="text-sm text-slate-600">
                        {new Date(payroll.createdDate).toLocaleDateString('tr-TR')}
                      </div>
                    </td>
                    <td className="p-5">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetail(payroll.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title={t('payroll.detail')}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteId(payroll.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title={t('payroll.delete')}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monthly Stats Chart */}
      {stats && stats.monthlyTotals.length > 0 && (
        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 p-8">
          <h3 className="text-lg font-black text-slate-800 mb-6">{t('payroll.monthlySummary')} - {selectedYear}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {stats.monthlyTotals.map(item => (
              <div key={item.month} className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <div className="text-xs font-bold text-slate-400 uppercase">{item.monthName}</div>
                <div className="text-lg font-black text-slate-800 mt-1">{formatCurrency(item.totalNet)}</div>
                <div className="text-xs text-slate-500 mt-1">{t('payroll.employeeCount', { count: item.employeeCount })}</div>
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* DETAILED REPORT TAB CONTENT */}
      {activeTab === 'detailed' && (
        <>
          {/* Filters for detailed report */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex gap-3 items-center flex-wrap">
              <Filter size={18} className="text-slate-400" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <select
                value={selectedMonth || new Date().getMonth() + 1}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {months.map((month, idx) => (
                  <option key={idx} value={idx + 1}>{month}</option>
                ))}
              </select>
              <select
                value={reportDepartmentId || ''}
                onChange={(e) => setReportDepartmentId(e.target.value ? Number(e.target.value) : null)}
                className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('payroll.allDepartments')}</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.departmentName}</option>
                ))}
              </select>

              {/* 2026-08-15 (kullanici talebi): santiye / lokasyon — puantajdaki bilesenin aynisi */}
              <div className="min-w-[320px]">
                <SiteLocationFilter
                  selectedSiteId={reportSiteId}
                  selectedLocationId={reportLocationId}
                  onSiteChange={setReportSiteId}
                  onLocationChange={setReportLocationId}
                />
              </div>

              {/* 2026-08-15 (kullanici talebi): tek personel bordrosu */}
              <select
                value={reportEmployeeId || ''}
                onChange={(e) => setReportEmployeeId(e.target.value ? Number(e.target.value) : null)}
                className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tüm Personel</option>
                {calcEmployees.map(p => (
                  <option key={p.id} value={p.id}>{p.ad}</option>
                ))}
              </select>

              {reportEmployeeId && (
                <button
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  className="bg-rose-600 text-white rounded-xl px-4 py-2.5 text-sm font-bold hover:bg-rose-700 transition-all disabled:opacity-50 flex items-center gap-2"
                  title="Seçili personelin bordrosunu PDF olarak indir"
                >
                  {exportingPdf ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                  PDF Bordro
                </button>
              )}
            </div>
          </div>

          {/* Detailed Report Table */}
          <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
            {loadingReport ? (
              <div className="p-20 text-center">
                <Loader2 size={48} className="mx-auto text-blue-500 animate-spin mb-4" />
                <p className="text-slate-400 font-bold">{t('payroll.calculatingPayroll')}</p>
              </div>
            ) : !detailedReport || detailedReport.rows.length === 0 ? (
              <div className="p-20 text-center">
                <Table size={48} className="mx-auto text-slate-200 mb-4" />
                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">{t('payroll.noDataForPeriod')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Report Header */}
                <div className="p-6 border-b border-slate-100 bg-slate-50">
                  <h3 className="text-lg font-black text-slate-800">{detailedReport.ayAdi} {t('payroll.payrollReport')}</h3>
                  <p className="text-sm text-slate-500">{t('payroll.personnelCount', { count: detailedReport.rows.length })}</p>
                </div>

                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-100 z-10">{t('payroll.registrationNo')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider sticky left-16 bg-slate-100 z-10">{t('payroll.fullName')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider">{t('payroll.department')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center bg-blue-50">{t('payroll.ssiAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center bg-red-50">{t('payroll.absentAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center bg-green-50">{t('payroll.paidLeaveAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center">{t('payroll.regularOtAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center text-red-600">{t('payroll.underworkAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center text-amber-600">{t('payroll.overtimeAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center text-purple-600">{t('payroll.weekendOtAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-center text-green-600">{t('payroll.holidayOtAbbr')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-right">{t('payroll.gross')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-right bg-amber-50">{t('payroll.otPay')}</th>
                      <th className="p-3 font-black text-slate-500 uppercase tracking-wider text-right bg-green-50">{t('payroll.total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detailedReport.rows.map(row => (
                      <tr key={row.employeeId} className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-mono text-slate-600 sticky left-0 bg-white z-10">{row.sicilNo}</td>
                        <td className="p-3 font-bold text-slate-800 sticky left-16 bg-white z-10 whitespace-nowrap">{row.adSoyad}</td>
                        <td className="p-3 text-slate-600">{row.bolum}</td>
                        <td className="p-3 text-center font-bold text-blue-600">{row.sgkGun}</td>
                        <td className="p-3 text-center font-bold text-red-600">{row.devamsizGun > 0 ? row.devamsizGun : '-'}</td>
                        <td className="p-3 text-center font-bold text-green-600">{row.ucretliIzinGun > 0 ? row.ucretliIzinGun : '-'}</td>
                        <td className="p-3 text-center font-mono">{row.normalMesaiStr || '-'}</td>
                        <td className="p-3 text-center font-mono text-red-600">{row.eksikCalismaStr || '-'}</td>
                        <td className="p-3 text-center font-mono text-amber-600">{row.fazlaMesaiStr || '-'}</td>
                        <td className="p-3 text-center font-mono text-purple-600">{row.haftaTatiliMesaiStr || '-'}</td>
                        <td className="p-3 text-center font-mono text-green-600">{row.resmiTatilMesaiStr || '-'}</td>
                        <td className="p-3 text-right font-bold">{formatCurrency(row.brutMaas)}</td>
                        <td className="p-3 text-right font-bold text-amber-600">{row.fazlaMesaiUcreti > 0 ? formatCurrency(row.fazlaMesaiUcreti) : '-'}</td>
                        <td className="p-3 text-right font-black text-green-700">{formatCurrency(row.toplamNet)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-black">
                      <td colSpan={3} className="p-4 text-slate-600">{t('payroll.totalWithCount', { count: detailedReport.summary.toplamPersonel })}</td>
                      <td className="p-4 text-center text-blue-600">{detailedReport.summary.toplamSgkGun}</td>
                      <td className="p-4 text-center text-red-600">{detailedReport.summary.toplamDevamsizGun}</td>
                      <td className="p-4 text-center text-green-600">{detailedReport.summary.toplamIzinGun}</td>
                      <td colSpan={5} className="p-4"></td>
                      <td className="p-4 text-right">{formatCurrency(detailedReport.summary.toplamBrutMaas)}</td>
                      <td className="p-4"></td>
                      <td className="p-4 text-right text-green-700">{formatCurrency(detailedReport.summary.toplamNetMaas)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-bold text-blue-600">{t('payroll.ssiAbbr')}</span>
                <span className="text-slate-500">{t('payroll.ssiDays')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-red-600">{t('payroll.absentAbbr')}</span>
                <span className="text-slate-500">{t('payroll.absentDays')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-green-600">{t('payroll.paidLeaveAbbr')}</span>
                <span className="text-slate-500">{t('payroll.paidLeave')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">{t('payroll.regularOtAbbr')}</span>
                <span className="text-slate-500">{t('payroll.regularOvertime')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-red-600">{t('payroll.underworkAbbr')}</span>
                <span className="text-slate-500">{t('payroll.underwork')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-amber-600">{t('payroll.overtimeAbbr')}</span>
                <span className="text-slate-500">{t('payroll.overtime50')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-purple-600">{t('payroll.weekendOtAbbr')}</span>
                <span className="text-slate-500">{t('payroll.weekendOvertime100')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-green-600">{t('payroll.holidayOtAbbr')}</span>
                <span className="text-slate-500">{t('payroll.holidayOvertime100')}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-8 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-black text-slate-800">{t('payroll.calculatePayroll')}</h3>
                <p className="text-slate-500 text-sm">{t('payroll.calculateDescription')}</p>
              </div>
              <button
                onClick={() => { setShowCalculator(false); setCalculation(null); }}
                className="p-2 hover:bg-slate-100 rounded-xl transition-all"
              >
                <X size={24} className="text-slate-400" />
              </button>
            </div>

            <div className="p-8 overflow-y-auto flex-1">
              {!calculation ? (
                <div className="max-w-md mx-auto space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">{t('payroll.year')}</label>
                    <select
                      value={calcYear}
                      onChange={(e) => setCalcYear(Number(e.target.value))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold"
                    >
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">{t('payroll.month')}</label>
                    <select
                      value={calcMonth}
                      onChange={(e) => setCalcMonth(Number(e.target.value))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold"
                    >
                      {months.map((month, idx) => (
                        <option key={idx} value={idx + 1}>{month}</option>
                      ))}
                    </select>
                  </div>
                  {/* 2026-08-15 (kullanici talebi): tek personel bordrosu */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Personel</label>
                    <select
                      value={calcEmployeeId}
                      onChange={(e) => setCalcEmployeeId(Number(e.target.value))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold"
                    >
                      <option value={0}>Tüm Personel</option>
                      {calcEmployees.map((p) => (
                        <option key={p.id} value={p.id}>{p.ad}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      {calcEmployeeId > 0
                        ? 'Yalnızca seçilen personel için bordro hesaplanır.'
                        : 'Tüm aktif personel için bordro hesaplanır.'}
                    </p>
                  </div>

                  <button
                    onClick={handleCalculate}
                    disabled={calculating}
                    className="w-full bg-blue-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-all disabled:opacity-50"
                  >
                    {calculating ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        {t('payroll.calculating')}
                      </>
                    ) : (
                      <>
                        <Calculator size={20} />
                        {t('payroll.calculate')}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Calculation Summary */}
                  <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-black text-blue-800">{calculation.monthName} {calculation.year}</h4>
                      <span className="text-sm text-blue-600 font-bold">
                        {t('payroll.employeeCount', { count: calculation.employeeCount })}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-blue-600 font-bold uppercase">{t('payroll.totalGross')}</div>
                        <div className="text-xl font-black text-blue-900">{formatCurrency(calculation.totalBaseSalary)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-green-600 font-bold uppercase">{t('payroll.totalAllowances')}</div>
                        <div className="text-xl font-black text-green-700">{formatCurrency(calculation.totalAllowances)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-red-600 font-bold uppercase">{t('payroll.totalDeductions')}</div>
                        <div className="text-xl font-black text-red-700">{formatCurrency(calculation.totalDeductions)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-600 font-bold uppercase">{t('payroll.totalNet')}</div>
                        <div className="text-xl font-black text-slate-900">{formatCurrency(calculation.totalNetSalary)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Employee Details */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="p-3 font-bold text-slate-600">{t('payroll.employee')}</th>
                          <th className="p-3 font-bold text-slate-600">{t('payroll.department')}</th>
                          <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.days')}</th>
                          <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.overtimeShort')}</th>
                          <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.gross')}</th>
                          <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.ssiAbbr')}</th>
                          {/* 2026-08-15 (kullanici talebi): Issizlik sigortasi kesintisi
                              tabloda hic gosterilmiyordu. Damga vergisi de eklendi ki
                              SGK + Issizlik + GV + Damga = toplam kesinti dogrulanabilsin. */}
                          <th className="p-3 font-bold text-slate-600 text-right">İşsizlik</th>
                          <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.incomeTax')}</th>
                          <th className="p-3 font-bold text-slate-600 text-right">Damga V.</th>
                          <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.net')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {calculation.employees.map(emp => (
                          <tr key={emp.employeeId} className="hover:bg-slate-50">
                            <td className="p-3">
                              <div className="font-bold text-slate-800">{emp.fullName}</div>
                              <div className="text-xs text-slate-400">{emp.employeeCode || '-'}</div>
                            </td>
                            <td className="p-3 text-slate-600">{emp.departmentName}</td>
                            <td className="p-3 text-right font-bold">{emp.workDays}</td>
                            <td className="p-3 text-right">
                              {emp.overtimeHours > 0 && (
                                <span className="text-amber-600 font-bold">{emp.overtimeHours}s</span>
                              )}
                            </td>
                            <td className="p-3 text-right font-bold">{formatCurrency(emp.baseSalary)}</td>
                            <td className="p-3 text-right text-red-600">{formatCurrency(emp.sgkEmployee)}</td>
                            <td className="p-3 text-right text-red-600">{formatCurrency((emp as any).unemploymentEmployee ?? 0)}</td>
                            <td className="p-3 text-right text-red-600">{formatCurrency(emp.incomeTax)}</td>
                            <td className="p-3 text-right text-red-600">{formatCurrency((emp as any).stampTax ?? 0)}</td>
                            <td className="p-3 text-right font-black text-slate-800">{formatCurrency(emp.netSalary)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {calculation && (
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <button
                  onClick={() => setCalculation(null)}
                  className="px-6 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-50"
                >
                  {t('payroll.back')}
                </button>
                <button
                  onClick={handleSavePayroll}
                  disabled={saving}
                  className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t('payroll.saving')}
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      {t('payroll.savePayroll')}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedPayroll && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-8 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-black text-slate-800">
                  {selectedPayroll.monthName} {selectedPayroll.year} {t('payroll.payrollSuffix')}
                </h3>
                <p className="text-slate-500 text-sm">{t('payroll.employeeCount', { count: selectedPayroll.employeeCount })}</p>
              </div>
              <button
                onClick={() => setSelectedPayroll(null)}
                className="p-2 hover:bg-slate-100 rounded-xl transition-all"
              >
                <X size={24} className="text-slate-400" />
              </button>
            </div>

            <div className="p-8 overflow-y-auto flex-1">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-slate-50 rounded-2xl p-4">
                  <div className="text-xs text-slate-500 font-bold uppercase">{t('payroll.totalGross')}</div>
                  <div className="text-xl font-black text-slate-800">{formatCurrency(selectedPayroll.totalBaseSalary)}</div>
                </div>
                <div className="bg-green-50 rounded-2xl p-4">
                  <div className="text-xs text-green-600 font-bold uppercase">{t('payroll.allowances')}</div>
                  <div className="text-xl font-black text-green-700">{formatCurrency(selectedPayroll.totalAllowances)}</div>
                </div>
                <div className="bg-red-50 rounded-2xl p-4">
                  <div className="text-xs text-red-600 font-bold uppercase">{t('payroll.deductions')}</div>
                  <div className="text-xl font-black text-red-700">{formatCurrency(selectedPayroll.totalDeductions)}</div>
                </div>
                <div className="bg-blue-50 rounded-2xl p-4">
                  <div className="text-xs text-blue-600 font-bold uppercase">{t('payroll.totalNet')}</div>
                  <div className="text-xl font-black text-blue-800">{formatCurrency(selectedPayroll.totalNetSalary)}</div>
                </div>
              </div>

              {/* Employee List */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="p-3 font-bold text-slate-600">{t('payroll.employee')}</th>
                      <th className="p-3 font-bold text-slate-600">{t('payroll.department')}</th>
                      <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.gross')}</th>
                      <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.allowances')}</th>
                      <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.deductions')}</th>
                      <th className="p-3 font-bold text-slate-600 text-right">{t('payroll.net')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {selectedPayroll.salaries.map(salary => (
                      <tr key={salary.salaryID} className="hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-bold text-slate-800">{salary.firstName} {salary.lastName}</div>
                          <div className="text-xs text-slate-400">{salary.employeeCode || '-'}</div>
                        </td>
                        <td className="p-3 text-slate-600">{salary.departmentName || '-'}</td>
                        <td className="p-3 text-right font-bold">{formatCurrency(salary.baseSalary || 0)}</td>
                        <td className="p-3 text-right text-green-600">{formatCurrency(salary.allowances || 0)}</td>
                        <td className="p-3 text-right text-red-600">{formatCurrency(salary.deductions || 0)}</td>
                        <td className="p-3 text-right font-black text-slate-800">{formatCurrency(salary.netSalary || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">{t('payroll.deletePayroll')}</h3>
              <p className="text-slate-500 mb-6">{t('payroll.deleteConfirmation')}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-50"
                >
                  {t('payroll.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Trash2 size={18} />
                  )}
                  {t('payroll.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;
